import { getAPI } from 'src/utils/api';
import i18n from 'i18next';
import { notification } from 'antd';

import checkAuthError from '../utils/checkAuthError';

notification.config({
	placement: 'topRight',
	top: 75,
	duration: 2,
});

const getAllActiveModels = async () => {
	try {
		const api = await getAPI();
		const { data } = await api.get('/model/all');
		const { models } = data;
		return models.filter((model) => model.isActive);
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};
const getAllModels = async () => {
	try {
		const api = await getAPI();
		const { data } = await api.get('/model/all');
		const { models } = data;
		return models;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};
const getModelConfig = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/id/${modelId}`);
		const { model } = data;
		return model;
	} catch (error) {
		checkAuthError(error);

		console.error('getModelConfig', error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};
const getModelConfigBySlug = async (modelSlug) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/slug/${modelSlug}`);
		const { model } = data;
		return model;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const getInfo = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/${modelId}/info`);
		const { info } = data;
		return info;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const getSVG = async (modelId, guid, revId = null) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/${modelId}/svg/${guid}/${revId}`);
		return data;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const uploadNewModel = async (modelData, modelFile) => {
	const { germanName, englishName, icon = 'fork', slug, isActive, navigationItems = [] } = modelData;

	// create object that is send to the server
	let formData;
	try {
		formData = new FormData();
		formData.append('name', 'modelFile');
		formData.append('modelFile', modelFile);
		formData.append('activateNewModel', true);
		formData.append('germanName', germanName);
		formData.append('englishName', englishName);
		formData.append('icon', icon);
		formData.append('slug', slug);
		formData.append('isActive', isActive);
		formData.append('navigationItems', navigationItems);
	} catch (error) {
		checkAuthError(error);

		console.log('Error at formData creation to send to server.');
		notification.error({
			message: i18n.t('newModel.mcer', 'Model creation error'),
			description: error,
		});
		throw error;
	}

	try {
		const api = await getAPI();
		const { data } = await api.post(`/model/new`, formData, { timeout: 60000 });

		notification.success({
			message: i18n.t('newModel.mdsc', 'Model data sucessfully uploaded'),
		});
		return data.model;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('newModel.mder', 'Model data could not be uploaded'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('newModel.mder', 'Model data could not be uploaded'),
				description: error,
			});
			throw error;
		}
	}
};

const uploadModelFile = async (modelId, modelFile, customRevName, activateNewModel) => {
	try {
		const formData = new FormData();
		formData.append('name', 'modelFile');
		formData.append('modelFile', modelFile);
		formData.append('customRevName', customRevName);
		formData.append('activateNewModel', activateNewModel);

		const api = await getAPI();
		const { data } = await api.post(`/model/${modelId}/data`, formData, { timeout: 60000 });
		notification.success({
			message: i18n.t('newModel.mdsc', 'Model data sucessfully uploaded'),
		});
		const { model } = data;
		return model;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('newModel.mder', 'Model data could not be uploaded'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('newModel.mder', 'Model data could not be uploaded'),
				description: error,
			});
			throw error;
		}
	}
};

const deleteModel = async (modelId) => {
	try {
		const api = await getAPI();
		const info = await api.delete(`/model/${modelId}/`);
		return info;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const updateModel = async (newModel) => {
	try {
		const api = await getAPI();
		const { data } = await api.put(`/model/${newModel._id}/`, newModel);
		const { model } = data;
		notification.success({
			message: i18n.t('notifications.modelUpdateSuccess', 'Model updated'),
		});
		// quick fix to prevent breaking. Here the views are returned as an array of strings, but needs array of objects
		return model;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.modelUpdateError', 'Model updated'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.modelUpdateError', 'Model updated'),
				description: error,
			});
			throw error;
		}
	}
};

const getMasterClassList = async (modelId, revision) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`model/${modelId}/masterClass/${revision}`);
		const { masterClasses } = data;
		return masterClasses;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const updateLayout = async (newLayout) => {
	try {
		const api = await getAPI();
		const { data } = await api.put(`layout/${newLayout._id}`, newLayout);
		const { layout } = data;
		notification.success({
			message: i18n.t('notifications.layoutUpdateSuccess', 'Layout sucessfully updated'),
		});
		return layout;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.layoutUpdateError', 'Layout sucessfully updated'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.layoutUpdateError', 'Layout sucessfully updated'),
				description: error,
			});
			throw error;
		}
	}
};

const getAuditStatus = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/${modelId}/audit/status`, {
			params: { processOwnerAttribute: 'Prozesseigner' },
		});

		const { audit, message } = data;
		return { audit, message };
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const getAuditStatusUser = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/${modelId}/audit/status/user`, {
			params: { processOwnerAttribute: 'Prozesseigner' },
		});
		const { audit, message } = data;
		return { audit, message };
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const authorizeAudit = async (modelId, processOwner, processId) => {
	try {
		const api = await getAPI();
		const { data } = await api.post(`/model/${modelId}/audit/authorize`, {
			processOwnerAttribute: processOwner,
			processId,
			unauthorize: false,
		});
		notification.success({
			message: i18n.t('notifications.authorizationSuccess', 'Success'),
		});
		return data;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.authorizationError', 'Error'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.authorizationError', 'Error'),
				description: error,
			});
			throw error;
		}
	}
};
const unauthorizeAudit = async (modelId, processOwner, processId) => {
	try {
		const api = await getAPI();
		const { data } = await api.post(`/model/${modelId}/audit/authorize`, {
			processOwnerAttribute: processOwner,
			processId,
			unauthorize: true,
		});
		notification.success({
			message: i18n.t('notifications.unAuthorizationSuccess', 'Success'),
		});
		return data;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.authorizationError', 'Error'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.authorizationError', 'Error'),
				description: error,
			});
			throw error;
		}
	}
};

const getLastChanges = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/${modelId}/audit/changes`);
		return data;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const getProcessOwner = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/${modelId}/audit/processOwners`, {
			params: { processOwnerAttribute: 'Prozesseigner' },
		});
		const { processOwners } = data;
		return processOwners;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const queryClasses = async (modelId, query) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`/model/${modelId}/iemclass/basicQuery?query=${JSON.stringify(query)}`);
		const { queryResults } = data;
		return queryResults;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const getQualityGates = async (modelId, qualityGateId, revision) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`model/${modelId}/qualityGates/${qualityGateId}/${revision}`);
		const { qualityGates } = data;
		return qualityGates;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const deleteRevision = async (modelId, revision) => {
	try {
		const api = await getAPI();
		const { data } = await api.delete(`model/${modelId}/revision/${revision}`);

		console.log('data', data);
		const { info } = data;
		return info;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};
const activateRevision = async (modelId, revision) => {
	try {
		const api = await getAPI();
		const { data } = await api.put(`model/${modelId}/revision/${revision}/activate`);

		const { info } = data;
		return info;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const exportModelAPI = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`model/${modelId}/export`, { responseType: 'blob', timeout: 60000 });

		return data;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const importModelAPI = async (file) => {
	try {
		const formData = new FormData();
		formData.append('name', 'paArchive');
		formData.append('paArchive', file);

		const api = await getAPI();
		const { data } = await api.post(`/model/import`, formData, { timeout: 60000 });
		const { model } = data;
		return model;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

export {
	getAllActiveModels,
	getAllModels,
	getModelConfig,
	getSVG,
	uploadNewModel,
	deleteModel,
	getModelConfigBySlug,
	updateModel,
	getMasterClassList,
	updateLayout,
	uploadModelFile,
	getAuditStatus,
	getAuditStatusUser,
	getInfo,
	authorizeAudit,
	unauthorizeAudit,
	getLastChanges,
	getProcessOwner,
	queryClasses,
	getQualityGates,
	deleteRevision,
	activateRevision,
	exportModelAPI,
	importModelAPI,
};
